import { useNavigate } from 'react-router-dom';
import { SupplierQualification } from '../../lib/api';
import type { ResponseRow } from './responseRows';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { Circle } from 'lucide-react';
import { useFormatters } from '../../contexts/PreferencesContext';
import { statusConfig } from './statusConfig';

interface MatrixCellProps {
  qual: SupplierQualification | undefined;
}

// Matrix cell with colored circle (matching old portal style)
export default function MatrixCell({ qual }: MatrixCellProps) {
  const navigate = useNavigate();
  const { formatDate } = useFormatters();

  if (!qual) {
    return <span className="text-muted-foreground">—</span>;
  }

  const config = statusConfig[qual.status] ?? { dotColor: '#6B7280', label: qual.status };
  const expiryDate = qual.expires_at ? formatDate(qual.expires_at) : null;
  const score = qual.score_percentage != null ? parseFloat(String(qual.score_percentage)).toFixed(0) + '%' : null;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button
            onClick={() => navigate((qual as Partial<ResponseRow>).review_href ?? `/intelligence/qualifications/${qual.id}`)}
            className="p-1 rounded-full hover:bg-muted/50 transition-colors"
          >
            <Circle
              className="h-6 w-6"
              fill={config.dotColor}
              stroke={config.dotColor}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1 text-sm">
            <div className="font-semibold">{config.label}</div>
            {expiryDate && (
              <div className="text-muted-foreground">
                Expiry: {expiryDate}
              </div>
            )}
            {score && (
              <div className="text-muted-foreground">
                Score: {score}
              </div>
            )}
            <div className="text-xs text-muted-foreground italic">
              Click to view details
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
